import { ConfidenceSlider } from '@/components/ConfidenceSlider'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Select } from '@/components/Select'
import { fromPercent, toPercent } from '@/lib/confidence'
import type { LookupTypeOption } from '@/lib/lookupTypes'
import type { FieldLookup } from '@/types/bundle'

export interface LookupResolutionSectionProps {
  lookup: FieldLookup
  onChange: (lookup: FieldLookup) => void
  /** Every distinct type currently in use in the Lookup Table (plus the two
   * legacy defaults) - `entry_type` must match one of these real values to
   * resolve anything at request time, so this is never a fixed, closed list
   * (src/lib/lookupTypes.ts, shared with the Lookup Table screen itself). */
  typeOptions: LookupTypeOption[]
}

// Visually nested under the field's own top-level settings (left border +
// indent), never a same-level peer - Design PRD 04 AC3. Only rendered by the
// caller when the field's config actually has a lookup block.
export function LookupResolutionSection({ lookup, onChange, typeOptions }: LookupResolutionSectionProps) {
  return (
    <div className="ml-4 flex flex-col gap-4 border-l-2 border-border pl-4">
      <h3 className="flex items-center gap-1 text-body font-medium text-text-primary">
        Lookup Table Resolution
        <InfoTooltip text="Looks up a matched name, like a solicitor firm, in the shared Lookup Table and replaces it with the official version." />
      </h3>

      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="lookup-entry-type">
          Entry type
          <InfoTooltip text="Which kind of entry to search for, such as a solicitor firm or estate agent." />
        </label>
        <Select
          id="lookup-entry-type"
          value={lookup.entry_type}
          onChange={(entry_type) => onChange({ ...lookup, entry_type })}
          options={typeOptions}
          className="w-64"
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
          Trigger below confidence
          <InfoTooltip text="If the extracted value's confidence is below this, the system tries to match it against the Lookup Table." />
        </span>
        <ConfidenceSlider
          value={toPercent(lookup.trigger_below_confidence)}
          onChange={(percent) => onChange({ ...lookup, trigger_below_confidence: fromPercent(percent) })}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
          Resolved confidence
          <InfoTooltip text="The confidence given to a value once it's matched in the Lookup Table." />
        </span>
        <ConfidenceSlider
          value={toPercent(lookup.resolved_confidence)}
          onChange={(percent) => onChange({ ...lookup, resolved_confidence: fromPercent(percent) })}
        />
      </div>
    </div>
  )
}
